const BASE = '/spring/aqua/api';

export function redirectToLogin() {
  // Dispatch event so AquaApp can show an in-app overlay rather than navigating away
  window.dispatchEvent(new CustomEvent('aqua-session-expired'));
}

export function goToLoginPage() {
  // Pass the full current URL (origin + path) so both owner and team member logins return here
  const returnTo = encodeURIComponent(window.location.href.split('?')[0]);
  window.location.href = '/spring/login?returnTo=' + returnTo;
}

export async function aquaLogout() {
  try { await fetch('/spring/aqua/api/logout', { method: 'POST' }); } catch (_) {}
  goToLoginPage();
}

async function req(path, options = {}) {
  const res = await fetch(BASE + path, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
    ...options,
  });
  if (res.redirected && res.url.includes('login')) { redirectToLogin(); return null; }
  if (res.status === 401 || res.status === 403) { redirectToLogin(); return null; }
  if (!res.ok) {
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('text/html')) { redirectToLogin(); return null; }
    throw new Error(`${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return null;
  const data = await res.json();
  if (data && data.error === 'Not authenticated') { redirectToLogin(); return null; }
  return data;
}

export const checkSession = () => req('/session');

export const aquaApi = {
  // Crops
  getCrops: () => req('/crops'),
  createCrop: (d) => req('/crops', { method: 'POST', body: JSON.stringify(d) }),
  updateCrop: (id, d) => req(`/crops/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteCrop: (id) => req(`/crops/${id}`, { method: 'DELETE' }),

  // Expense Types
  getExpenseTypes: () => req('/expense-types'),
  createExpenseType: (d) => req('/expense-types', { method: 'POST', body: JSON.stringify(d) }),
  updateExpenseType: (id, d) => req(`/expense-types/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteExpenseType: (id) => req(`/expense-types/${id}`, { method: 'DELETE' }),

  // Expenses
  getExpenses: (cropId) => req('/expenses' + (cropId ? `?cropId=${cropId}` : '')),
  createExpense: (d) => req('/expenses', { method: 'POST', body: JSON.stringify(d) }),
  updateExpense: (id, d) => req(`/expenses/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteExpense: (id) => req(`/expenses/${id}`, { method: 'DELETE' }),
  getExpenseSummary: (cropId) => req(`/report/expense-summary?cropId=${cropId}`),

  // Feed
  getFeed: (cropId) => req('/feed' + (cropId ? `?cropId=${cropId}` : '')),
  createFeed: (d) => req('/feed', { method: 'POST', body: JSON.stringify(d) }),
  updateFeed: (id, d) => req(`/feed/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteFeed: (id) => req(`/feed/${id}`, { method: 'DELETE' }),
  getFeedSummary: (cropId) => req(`/report/feed-summary?cropId=${cropId}`),

  // Chemicals
  getChem: (cropId) => req('/chem' + (cropId ? `?cropId=${cropId}` : '')),
  createChem: (d) => req('/chem', { method: 'POST', body: JSON.stringify(d) }),
  updateChem: (id, d) => req(`/chem/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteChem: (id) => req(`/chem/${id}`, { method: 'DELETE' }),
  getChemSummary: (cropId) => req(`/report/chem-summary?cropId=${cropId}`),

  // Sampling
  getSampling: (cropId) => req('/sampling' + (cropId ? `?cropId=${cropId}` : '')),
  createSampling: (d) => req('/sampling', { method: 'POST', body: JSON.stringify(d) }),
  updateSampling: (id, d) => req(`/sampling/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteSampling: (id) => req(`/sampling/${id}`, { method: 'DELETE' }),
  getSamplingProgress: (cropId) => req(`/report/sampling-progress?cropId=${cropId}`),

  // Equipment
  getEquipment: () => req('/equipment'),
  createEquipment: (d) => req('/equipment', { method: 'POST', body: JSON.stringify(d) }),
  updateEquipment: (id, d) => req(`/equipment/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteEquipment: (id) => req(`/equipment/${id}`, { method: 'DELETE' }),

  // Cost Impact
  getCostImpact: (cropId) => req(`/report/cost-impact?cropId=${cropId}`),

  // All-crops investment summary
  getAllCropsSummary: () => req('/report/all-crops-summary'),

  // Suppliers
  getSuppliers: () => req('/suppliers'),
  createSupplier: (d) => req('/suppliers', { method: 'POST', body: JSON.stringify(d) }),
  updateSupplier: (id, d) => req(`/suppliers/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteSupplier: (id) => req(`/suppliers/${id}`, { method: 'DELETE' }),

  // Purchases
  getPurchases: (cropId) => req('/purchases' + (cropId ? `?cropId=${cropId}` : '')),
  getPurchaseItems: (id) => req(`/purchases/${id}/items`),
  createPurchase: (d) => req('/purchases', { method: 'POST', body: JSON.stringify(d) }),
  updatePurchase: (id, d) => req(`/purchases/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deletePurchase: (id) => req(`/purchases/${id}`, { method: 'DELETE' }),

  // Feed Stock
  getFeedStock: (cropId) => req('/report/feed-stock' + (cropId ? `?cropId=${cropId}` : '')),

  // Chem product names (for feed log dropdown)
  getChemProducts: (cropId) => req('/chem/products' + (cropId ? `?cropId=${cropId}` : '')),

  // Chem-based stock balance (chem purchases minus feed log usage)
  getChemStock: (cropId) => req('/report/chem-stock' + (cropId ? `?cropId=${cropId}` : '')),

  // Documents — lists s3_objects for the chmr/ prefix
  listDocs: (folder) => req('/docs' + (folder ? `?folder=${encodeURIComponent(folder)}` : '')),

  // Harvest
  getHarvestExpenses:   (cropId) => req(`/harvest/expenses?cropId=${cropId}`),
  createHarvestExpense: (d) => req('/harvest/expenses', { method: 'POST', body: JSON.stringify(d) }),
  updateHarvestExpense: (id, d) => req(`/harvest/expenses/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteHarvestExpense: (id) => req(`/harvest/expenses/${id}`, { method: 'DELETE' }),

  getHarvestSales:   (cropId) => req(`/harvest/sales?cropId=${cropId}`),
  createHarvestSale: (d) => req('/harvest/sales', { method: 'POST', body: JSON.stringify(d) }),
  updateHarvestSale: (id, d) => req(`/harvest/sales/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteHarvestSale: (id) => req(`/harvest/sales/${id}`, { method: 'DELETE' }),

  getHarvestMiscIncome:   (cropId) => req(`/harvest/misc-income?cropId=${cropId}`),
  createHarvestMiscIncome: (d) => req('/harvest/misc-income', { method: 'POST', body: JSON.stringify(d) }),
  updateHarvestMiscIncome: (id, d) => req(`/harvest/misc-income/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteHarvestMiscIncome: (id) => req(`/harvest/misc-income/${id}`, { method: 'DELETE' }),

  getHarvestReceipts:   (cropId) => req(`/harvest/receipts?cropId=${cropId}`),
  createHarvestReceipt: (d) => req('/harvest/receipts', { method: 'POST', body: JSON.stringify(d) }),
  updateHarvestReceipt: (id, d) => req(`/harvest/receipts/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteHarvestReceipt: (id) => req(`/harvest/receipts/${id}`, { method: 'DELETE' }),

  getHarvestSummary: (cropId) => req(`/harvest/summary?cropId=${cropId}`),

  // Feed Purchases
  getFeedPurchases: (cropId) => req('/feed-purchases' + (cropId ? `?cropId=${cropId}` : '')),
  createFeedPurchase: (d) => req('/feed-purchases', { method: 'POST', body: JSON.stringify(d) }),
  updateFeedPurchase: (id, d) => req(`/feed-purchases/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteFeedPurchase: (id) => req(`/feed-purchases/${id}`, { method: 'DELETE' }),

  // Feed Names master
  getFeedNames: () => req('/feed-names'),
  createFeedName: (d) => req('/feed-names', { method: 'POST', body: JSON.stringify(d) }),
  updateFeedName: (id, d) => req(`/feed-names/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteFeedName: (id) => req(`/feed-names/${id}`, { method: 'DELETE' }),

  // Chem Names master
  getChemNames: () => req('/chem-names'),
  createChemName: (d) => req('/chem-names', { method: 'POST', body: JSON.stringify(d) }),
  updateChemName: (id, d) => req(`/chem-names/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteChemName: (id) => req(`/chem-names/${id}`, { method: 'DELETE' }),

  // Product Names master (unified — replaces feed_names + chem_names)
  getProductNames: (category) => req('/product-names' + (category ? `?category=${encodeURIComponent(category)}` : '')),
  createProductName: (d) => req('/product-names', { method: 'POST', body: JSON.stringify(d) }),
  updateProductName: (id, d) => req(`/product-names/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteProductName: (id) => req(`/product-names/${id}`, { method: 'DELETE' }),

  // Categories master
  getCategories: () => req('/categories'),
  createCategory: (d) => req('/categories', { method: 'POST', body: JSON.stringify(d) }),
  deleteCategory: (id) => req(`/categories/${id}`, { method: 'DELETE' }),

  // Stock Purchases (owner-level — no crop_id)
  getStockPurchases: () => req('/stock-purchases'),
  createStockPurchase: (d) => req('/stock-purchases', { method: 'POST', body: JSON.stringify(d) }),
  updateStockPurchase: (id, d) => req(`/stock-purchases/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteStockPurchase: (id) => req(`/stock-purchases/${id}`, { method: 'DELETE' }),

  // Purchase Interest
  getPurchaseInterest: () => req('/purchase-interest'),
  createPurchaseInterest: (d) => req('/purchase-interest', { method: 'POST', body: JSON.stringify(d) }),
  updatePurchaseInterest: (id, d) => req(`/purchase-interest/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deletePurchaseInterest: (id) => req(`/purchase-interest/${id}`, { method: 'DELETE' }),

  // Ponds master (farm-level, independent of crops)
  getPonds: () => req('/ponds'),
  createPond: (d) => req('/ponds', { method: 'POST', body: JSON.stringify(d) }),
  updatePond: (id, d) => req(`/ponds/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deletePond: (id) => req(`/ponds/${id}`, { method: 'DELETE' }),

  // Crop Release Dates
  getCropRelease: (cropId) => req('/crop-release' + (cropId ? `?cropId=${cropId}` : '')),
  saveCropRelease: (d) => req('/crop-release', { method: 'POST', body: JSON.stringify(d) }),
  deleteCropRelease: (id) => req(`/crop-release/${id}`, { method: 'DELETE' }),

  // Pond Stocking
  getStocking: (cropId) => req('/stocking' + (cropId ? `?cropId=${cropId}` : '')),
  createStocking: (d) => req('/stocking', { method: 'POST', body: JSON.stringify(d) }),
  updateStocking: (id, d) => req(`/stocking/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteStocking: (id) => req(`/stocking/${id}`, { method: 'DELETE' }),

  // Chemical Usage Log
  getChemUsage: (cropId) => req('/chem-usage' + (cropId ? `?cropId=${cropId}` : '')),
  createChemUsage: (d) => req('/chem-usage', { method: 'POST', body: JSON.stringify(d) }),
  updateChemUsage: (id, d) => req(`/chem-usage/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteChemUsage: (id) => req(`/chem-usage/${id}`, { method: 'DELETE' }),

  // Owner stock balance
  getOwnerStock: () => req('/report/owner-stock'),

  // Feed FRP — latest price per feed name for a crop (used in feed log cost calc)
  getFeedFrp: (cropId) => req(`/report/feed-frp?cropId=${cropId}`),

  // Supplier Payments
  getSupplierPayments: (supplierId) => req('/supplier-payments' + (supplierId ? `?supplierId=${supplierId}` : '')),
  createSupplierPayment: (d) => req('/supplier-payments', { method: 'POST', body: JSON.stringify(d) }),
  updateSupplierPayment: (id, d) => req(`/supplier-payments/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteSupplierPayment: (id) => req(`/supplier-payments/${id}`, { method: 'DELETE' }),
  getSupplierLedger: () => req('/report/supplier-ledger'),

  // Owner settings
  getSettings: () => req('/settings'),
  saveSetting: (d) => req('/settings', { method: 'PUT', body: JSON.stringify(d) }),

  // Users (employee management — admin only for write ops)
  getUsers: () => req('/users'),
  createUser: (d) => req('/users', { method: 'POST', body: JSON.stringify(d) }),
  updateUser: (id, d) => req(`/users/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteUser: (id) => req(`/users/${id}`, { method: 'DELETE' }),
};
